/** Docs (UA): https://prostopay.atlassian.net/wiki/spaces/SW/pages/1902542854 — WishesPage */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { ContentBox, PrimaryButton, ScreenTitle, ScreenWrapper } from '../../../../shared/ui';
import { useAppStore } from '../../../../shared/hooks/useAppStore';
import { debug } from '../../../../shared/lib/general';
import { CustomTextField } from '../../components/CustomTextField';
import styles from '../../components/FeedbacksStyles.module.css';
import {
    COMPANY_IDS_FOR_FEEDBACKS,
    DISABLE_OR_ENABLE_BUTTON,
    FEEDBACKS_CUSTOMER_STATUS,
    FEEDBACK_ROUTES,
} from '../../feedback.constants';
import { UpdateFeedback } from '../../feedback.types';
import { useFeedbackStore } from '../../hooks/useFeedbackStore';
import { useUpdateFeedbacks } from '../../hooks/useUpdateFeedbacks';

interface FieldsDataState {
    Wishes: string;
}

export const WishesPage = () => {
    const vm = useAppStore((s) => s.vm);
    const feedback = useFeedbackStore((s) => s.feedback);
    const updatedFeedback = useFeedbackStore((s) => s.updateFeedback);
    const setUpdateFeedback = useFeedbackStore((s) => s.setUpdateFeedback);
    const setPageNumber = useFeedbackStore((s) => s.setPageNumber);
    const setNumberVisibility = useFeedbackStore((s) => s.setPageNumberVisible);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [notes, setNotes] = useState<FieldsDataState>({
        Wishes: '',
    });
    const [hasError, setHasError] = useState<boolean>(false);
    const isNextButtonDisabled =
        notes.Wishes === '' && !COMPANY_IDS_FOR_FEEDBACKS.includes(vm.companyId);
    const nextButtonStyles =
        isNextButtonDisabled || hasError
            ? DISABLE_OR_ENABLE_BUTTON[1]
            : DISABLE_OR_ENABLE_BUTTON[0];

    const updateFeedbacksMutation = useUpdateFeedbacks();

    useEffect(() => {
        setPageNumber('2/3');
        setNumberVisibility(true);
    }, []);

    const handleNotesChange = (event: {
        target: { id: string; value: string };
    }) => {
        const { id, value } = event.target;
        setNotes({
            Wishes: id === 'Wishes' ? value : notes.Wishes,
        });
    };

    const onNextPage = async () => {
        const newFeedback: UpdateFeedback = {
            ...updatedFeedback,
            custom_content: notes.Wishes ? notes.Wishes : null,
            customer_status: FEEDBACKS_CUSTOMER_STATUS.AFTER_WISHES_PAGE,
        };
        setUpdateFeedback(newFeedback);
        try {
            await updateFeedbacksMutation.mutateAsync({ id: feedback.id, body: newFeedback });
        } catch (e) {
            debug(`Wishes submit failed: ${String(e)}`, 'error');
        }
        navigate(generatePath(FEEDBACK_ROUTES.contact, { id: vm.qr }));
    };

    const onCancelClick = () => {
        setNumberVisibility(false);
        navigate(generatePath(FEEDBACK_ROUTES.complete, { id: vm.qr }), {
            replace: true,
        });
    };

    return (
        <ScreenWrapper>
            <ScreenTitle>{t('Your wishes')}</ScreenTitle>
            <ContentBox>
                <CustomTextField
                    id="Wishes"
                    label={t('Your comment')}
                    onChange={handleNotesChange}
                    rows={5}
                    setGlobalError={setHasError}
                />

                <div className={styles.feedbacksButtonBox}>
                    <PrimaryButton
                        className={styles.feedbacksButton}
                        onClick={onCancelClick}
                    >
                        {t('Cancel')}
                    </PrimaryButton>
                    <PrimaryButton
                        className={styles.feedbacksButton}
                        disabled={isNextButtonDisabled || hasError || updateFeedbacksMutation.isPending}
                        loading={updateFeedbacksMutation.isPending}
                        onClick={onNextPage}
                        style={nextButtonStyles}
                    >
                        {t('Next')}
                    </PrimaryButton>
                </div>
            </ContentBox>
        </ScreenWrapper>
    );
};
